/**
 * COBRA administration dashboard.
 *
 * Single-page admin surface that mirrors what Chris at CBIZ asked
 * for: at-a-glance counts + monthly liability up top, the active /
 * pending / expiring lists below, with one-click actions for the
 * common admin moves (record election, record payment, resend
 * notice).
 *
 * This page is HR-admin scoped — the route is gated to
 * MANAGE_EMPLOYEES at both the SPA + the API layer.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  Loader2,
  Mail,
  RefreshCw,
  ShieldAlert,
  TrendingUp,
  Users,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  backfillCobraFromTerminations,
  getCobraSummary,
  listCobraEvents,
  recordCobraElection,
  resendCobraNotice,
  type CobraEvent,
  type CobraStatus,
  type CobraSummary,
} from "@/api/cobra";

const STATUS_LABELS: Record<CobraStatus, { label: string; tone: "default" | "info" | "success" | "warning" | "danger" }> = {
  pending_notice: { label: "Pending notice", tone: "warning" },
  notice_sent: { label: "Awaiting election", tone: "info" },
  elected: { label: "Elected", tone: "info" },
  declined: { label: "Declined", tone: "default" },
  expired: { label: "Expired", tone: "default" },
  active: { label: "Active", tone: "success" },
  terminated: { label: "Terminated", tone: "default" },
};

function fmtMoney(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  // Date-only — no timezone shift.
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function daysFromNow(iso: string | null): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  return Math.ceil((target - Date.now()) / (1000 * 60 * 60 * 24));
}


export function CobraPage() {
  const { addToast } = useToast();
  const [summary, setSummary] = useState<CobraSummary | null>(null);
  const [events, setEvents] = useState<CobraEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    // Fetch independently so a backend hiccup on one endpoint doesn't
    // blank the whole page. We also fall back to a calm empty payload
    // (rather than a toast) when both fail — the page still renders
    // the "no participants" empty state, which is the right message
    // for a brand-new tenant with no COBRA events yet.
    const [s, e] = await Promise.allSettled([
      getCobraSummary(),
      listCobraEvents(),
    ]);
    if (s.status === "fulfilled") {
      setSummary(s.value);
    } else {
      setSummary({
        active_count: 0,
        pending_election_count: 0,
        pending_notice_count: 0,
        elected_awaiting_payment_count: 0,
        monthly_liability: 0,
        late_payment_count: 0,
        expiring_30_count: 0,
        next_election_deadline: null,
      });
      console.warn("CobraPage: summary fetch failed", s.reason);
    }
    if (e.status === "fulfilled") {
      setEvents(e.value);
    } else {
      setEvents([]);
      console.warn("CobraPage: events fetch failed", e.reason);
    }
    // Both rejected → log it for diagnostics but DON'T toast.
    // The page renders fine with empty defaults; a worrying toast on
    // a brand-new tenant where COBRA simply has no events yet is
    // user-hostile. If you need to debug a real outage, the console
    // warnings above carry the actual error.
    if (s.status === "rejected" && e.status === "rejected") {
      console.warn(
        "CobraPage: both summary + events fetches failed — rendering empty state",
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = useMemo(() => {
    const byStatus: Record<string, CobraEvent[]> = {
      active: [],
      notice_sent: [],
      elected: [],
      pending_notice: [],
      expired: [],
      declined: [],
      terminated: [],
    };
    for (const e of events ?? []) {
      (byStatus[e.status] ?? (byStatus[e.status] = [])).push(e);
    }
    return byStatus;
  }, [events]);

  const expiringSoon = useMemo(
    () =>
      (events ?? [])
        .filter(
          (e) =>
            e.status === "active" &&
            e.coverage_end_date &&
            (daysFromNow(e.coverage_end_date) ?? 99) <= 60,
        )
        .sort(
          (a, b) =>
            (a.coverage_end_date || "").localeCompare(b.coverage_end_date || ""),
        ),
    [events],
  );

  const handleElection = async (event: CobraEvent, elected: boolean) => {
    setBusyId(event.id);
    try {
      await recordCobraElection(event.id, elected);
      addToast("success", elected ? "Marked as elected" : "Marked as declined");
      await reload();
    } catch {
      addToast("error", "Couldn't record election");
    } finally {
      setBusyId(null);
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await backfillCobraFromTerminations();
      // The backend now ALWAYS returns 200 — even on error — with an
      // ``error`` string populated. Branch on the structured payload
      // instead of a generic catch so the user sees what actually
      // happened rather than a useless "Sync failed".
      if (r.error) {
        console.warn("cobra backfill returned error", r);
        addToast(
          "error",
          `Sync had a problem: ${r.error.slice(0, 200)}` +
            (r.created > 0
              ? ` — but ${r.created} event(s) were still created.`
              : ""),
        );
      } else if (r.created > 0) {
        addToast(
          "success",
          `Created ${r.created} COBRA event${r.created === 1 ? "" : "s"} ` +
            `(${r.skipped_existing} already had one)`,
        );
      } else if (r.total_terminated === 0) {
        addToast(
          "info",
          "No terminated employees in this org yet.",
        );
      } else {
        addToast(
          "info",
          `All ${r.total_terminated} terminated employees already have COBRA events.`,
        );
      }
      await reload();
    } catch (err: unknown) {
      // Only network errors / hard 4xx-5xx hit this branch. The
      // global FastAPI exception handler embeds the exception CLASS
      // NAME + a request_id in the 500 body — surface BOTH so we can
      // diagnose without Render logs.
      const ax = err as {
        response?: {
          status?: number;
          data?: {
            detail?: unknown;
            exception?: string;
            request_id?: string;
          };
        };
        message?: string;
      };
      const status = ax?.response?.status;
      const detail = ax?.response?.data?.detail;
      const exceptionClass = ax?.response?.data?.exception;
      const requestId = ax?.response?.data?.request_id;
      console.warn("cobra backfill threw", {
        status,
        detail,
        exceptionClass,
        requestId,
        err,
      });
      let detailStr: string;
      if (status === 403) {
        detailStr = "You don't have permission to sync COBRA.";
      } else if (status === 404) {
        detailStr =
          "Backfill endpoint not found — your deploy may still be initializing.";
      } else if (exceptionClass) {
        // Production: exception class is included; message is hidden.
        // Class name alone is enough to diagnose 90% of cases.
        detailStr = `${exceptionClass}${
          requestId ? ` (request ${requestId.slice(0, 8)})` : ""
        }`;
      } else if (typeof detail === "string") {
        detailStr = detail;
      } else {
        detailStr = ax?.message || "Network error";
      }
      addToast("error", `Sync failed: ${detailStr}`);
    } finally {
      setBackfilling(false);
    }
  };

  const handleResendNotice = async (event: CobraEvent) => {
    setBusyId(event.id);
    try {
      await resendCobraNotice(event.id);
      addToast("success", "Notice re-sent");
      await reload();
    } catch {
      addToast("error", "Couldn't re-send notice");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <PageHeader
          title="COBRA"
          subtitle="Continuation-of-coverage tracking, notices, and payments"
        />
        {/* Escape hatch — calls /cobra/backfill-from-terminations to
            create COBRA events for any TERMINATED employee that
            doesn't have one. Idempotent. The auto-trigger on offboard
            should handle this normally; this button rescues the rare
            case where it didn't (botched deploy, partial migration,
            employee terminated before the COBRA module shipped). */}
        <Button
          size="sm"
          variant="secondary"
          onClick={handleBackfill}
          disabled={backfilling}
          title="Create COBRA events for any terminated employee that doesn't have one"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${backfilling ? "animate-spin" : ""}`}
          />
          Sync from terminations
        </Button>
      </div>

      {/* Top-of-page tile bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Tile
          icon={<Users className="h-4 w-4" />}
          label="Active participants"
          value={summary?.active_count ?? "—"}
        />
        <Tile
          icon={<DollarSign className="h-4 w-4" />}
          label="Monthly liability"
          value={summary ? fmtMoney(summary.monthly_liability) : "—"}
          tone={
            summary && summary.monthly_liability > 0 ? "info" : undefined
          }
        />
        <Tile
          icon={<Clock className="h-4 w-4" />}
          label="Pending elections"
          value={summary?.pending_election_count ?? "—"}
          tone={
            summary && summary.pending_election_count > 0
              ? "warning"
              : undefined
          }
        />
        <Tile
          icon={<TrendingUp className="h-4 w-4" />}
          label="Ending in 30 days"
          value={summary?.expiring_30_count ?? "—"}
        />
      </div>

      {loading && !events ? (
        <Card>
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        </Card>
      ) : (
        <>
          {/* Pending notice (manual review) — events created by an
              offboarding where the rules engine couldn't auto-resolve a
              jurisdiction (e.g. <20 employees + no recognized state).
              The notice was NOT auto-sent; admin reviews + clicks
              "Resend notice" once they've confirmed details. */}
          {groups.pending_notice.length > 0 && (
            <Section
              title="Pending notice (manual review)"
              icon={<ShieldAlert className="h-4 w-4 text-warning" />}
            >
              {groups.pending_notice.map((evt) => (
                <Row
                  key={evt.id}
                  event={evt}
                  busy={busyId === evt.id}
                  primaryActions={
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleResendNotice(evt)}
                        disabled={busyId === evt.id}
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Send notice
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleElection(evt, false)}
                        disabled={busyId === evt.id}
                      >
                        Mark declined
                      </Button>
                    </>
                  }
                />
              ))}
            </Section>
          )}

          {/* Pending elections — admin's most-actionable list */}
          <Section
            title="Pending elections"
            icon={<Clock className="h-4 w-4 text-warning" />}
            empty={
              groups.notice_sent.length === 0
                ? "No notices waiting on a response."
                : null
            }
          >
            {groups.notice_sent.map((evt) => (
              <Row
                key={evt.id}
                event={evt}
                busy={busyId === evt.id}
                primaryActions={
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleResendNotice(evt)}
                      disabled={busyId === evt.id}
                    >
                      <Mail className="h-3.5 w-3.5" />
                      Resend notice
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleElection(evt, true)}
                      disabled={busyId === evt.id}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Mark elected
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleElection(evt, false)}
                      disabled={busyId === evt.id}
                    >
                      Mark declined
                    </Button>
                  </>
                }
              />
            ))}
          </Section>

          {/* Active participants — both ACTIVE (paid + on coverage)
              and ELECTED (admin recorded election but no payment
              tracked) land here so the admin's "Mark elected" click
              produces a visible move. record_election now sets
              status=ACTIVE directly for new clicks, but legacy rows
              + payment-tracking workflows can still produce ELECTED
              and we want those to show up too. */}
          <Section
            title="Active participants"
            icon={<CheckCircle2 className="h-4 w-4 text-success" />}
            empty={
              groups.active.length === 0 && groups.elected.length === 0
                ? "No one is currently on COBRA coverage."
                : null
            }
          >
            {[...groups.active, ...groups.elected].map((evt) => (
              <Row
                key={evt.id}
                event={evt}
                busy={busyId === evt.id}
                showCoverageEnd
              />
            ))}
          </Section>

          {/* Coverage ending soon */}
          {expiringSoon.length > 0 && (
            <Section
              title="Coverage ending in next 60 days"
              icon={<AlertTriangle className="h-4 w-4 text-warning" />}
            >
              {expiringSoon.map((evt) => (
                <Row key={evt.id} event={evt} showCoverageEnd />
              ))}
            </Section>
          )}

          {/* Closed events — collapsed below for completeness */}
          {(groups.declined.length > 0 ||
            groups.expired.length > 0 ||
            groups.terminated.length > 0) && (
            <Section
              title="Closed"
              icon={<ShieldAlert className="h-4 w-4 text-text-muted" />}
            >
              {[...groups.declined, ...groups.expired, ...groups.terminated].map(
                (evt) => (
                  <Row key={evt.id} event={evt} muted />
                ),
              )}
            </Section>
          )}
        </>
      )}
    </motion.div>
  );
}

interface TileProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  tone?: "info" | "warning" | "success";
}

function Tile({ icon, label, value, tone }: TileProps) {
  const toneClass =
    tone === "warning"
      ? "border-warning/30 bg-warning/5"
      : tone === "info"
        ? "border-accent/30 bg-accent/5"
        : tone === "success"
          ? "border-success/30 bg-success/5"
          : "border-border/40 bg-surface/60";
  return (
    <div className={`rounded-md border ${toneClass} p-3`}>
      <div className="flex items-center gap-1.5 text-xs text-text-muted">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold text-text-primary">
        {value}
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  empty?: string | null;
  children?: React.ReactNode;
}

function Section({ title, icon, empty, children }: SectionProps) {
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            {icon}
            {title}
          </span>
        </CardTitle>
      </CardHeader>
      {empty ? (
        <div className="py-6 text-center text-sm text-text-muted">
          <FileText className="h-6 w-6 mx-auto mb-2 opacity-40" />
          {empty}
        </div>
      ) : (
        <ul className="divide-y divide-border/40">{children}</ul>
      )}
    </Card>
  );
}

interface RowProps {
  event: CobraEvent;
  busy?: boolean;
  primaryActions?: React.ReactNode;
  showCoverageEnd?: boolean;
  muted?: boolean;
}

function Row({
  event,
  busy,
  primaryActions,
  showCoverageEnd,
  muted,
}: RowProps) {
  const status = STATUS_LABELS[event.status];
  const days = event.election_deadline
    ? daysFromNow(event.election_deadline)
    : null;
  return (
    <li
      className={
        "py-3 flex items-start justify-between gap-3 " +
        (muted ? "opacity-70" : "")
      }
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-text-primary truncate">
            {event.employee_name ?? "Unknown employee"}
          </span>
          {status && <Badge variant={status.tone}>{status.label}</Badge>}
          <Badge variant="default">
            {event.jurisdiction.toUpperCase()}
          </Badge>
          {event.coverage_duration_months ? (
            <span className="text-xs text-text-muted">
              {event.coverage_duration_months} mo
            </span>
          ) : null}
        </div>
        <p className="text-xs text-text-muted mt-0.5">
          {event.plan_summary
            ? `${event.plan_summary} · `
            : event.elections?.length
              ? ""
              : "No plan info on file · "}
          Premium {fmtMoney(event.monthly_premium)}/mo
          {event.election_deadline && (
            <>
              {" · "}Election by {fmtDate(event.election_deadline)}
              {days !== null && days >= 0 && days <= 14 && (
                <span className="text-warning"> · {days}d left</span>
              )}
            </>
          )}
          {showCoverageEnd && event.coverage_end_date && (
            <> · Ends {fmtDate(event.coverage_end_date)}</>
          )}
        </p>

        {/* Per-plan election breakdown — populated from
            benefit_elections at offboard time. Shows nothing for
            legacy events that pre-date the elections column. */}
        {event.elections && event.elections.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs">
            {event.elections.map((line, idx) => (
              <li
                key={line.election_id || idx}
                className="flex items-center gap-2 text-text-muted"
              >
                <span className="text-text-primary">
                  {line.plan_name || "(plan)"}
                </span>
                {line.tier && line.tier !== "waived" && (
                  <span>
                    ({line.tier.replace(/_/g, " ")})
                  </span>
                )}
                <span>·</span>
                <span className="tabular-nums text-text-primary">
                  {fmtMoney(line.monthly_premium)}/mo
                </span>
                {line.base_monthly_cost > 0 && (
                  <span className="text-[11px]">
                    (102% of {fmtMoney(line.base_monthly_cost)})
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {busy && <Loader2 className="h-4 w-4 animate-spin text-text-muted" />}
        {primaryActions}
      </div>
    </li>
  );
}

export default CobraPage;
